import React, { useState, useEffect, useCallback } from 'react';
import { Video, Calendar, Plus, Link2, Copy, Check, ArrowRight, Lock, Clock, Loader2 } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../hooks/useAuth';
import { Meeting } from '../types';
import { api } from '../lib/api';

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [joinCode, setJoinCode] = useState('');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [title, setTitle] = useState('');
  const [password, setPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const loadMeetings = useCallback(async () => {
    try {
      const data = await api.getMeetings();
      setMeetings(data);
    } catch (e) {
      console.error('Failed to load meetings:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMeetings();
  }, [loadMeetings]);

  const handleInstantMeeting = async () => {
    setCreating(true);
    try {
      const data = await api.createMeeting('Instant Meeting');
      window.location.hash = `#/meeting/${data.publicId}`;
    } catch (e: any) {
      alert(e.message || 'Failed to create meeting');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateMeeting = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const data = await api.createMeeting(title || 'Scheduled Meeting', password || undefined);
      setShowScheduleModal(false);
      setTitle('');
      setPassword('');
      await loadMeetings();
      window.location.hash = `#/meeting/${data.publicId}`;
    } catch (e: any) {
      alert(e.message || 'Failed to create meeting');
    } finally {
      setCreating(false);
    }
  };

  const handleJoinByCode = (e: React.FormEvent) => {
    e.preventDefault();
    if (!joinCode.trim()) return;
    const clean = joinCode.trim().replace(/^.*\/meeting\//, '').replace(/[^A-Za-z0-9_-]/g, '');
    window.location.hash = `#/meeting/${clean}`;
  };

  const copyMeetingLink = (publicId: string) => {
    const url = `${window.location.origin}/#/meeting/${publicId}`;
    navigator.clipboard.writeText(url);
    setCopiedId(publicId);
    setTimeout(() => setCopiedId(null), 2500);
  };

  return (
    <div className="min-h-screen bg-[#090b10] flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-6xl w-full mx-auto p-6 md:p-8 space-y-8">
        {/* Welcome Hero */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-slate-800/80 pb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
              Hello, {user?.name || 'there'} 👋
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Cloudflare-native video conferencing powered by Realtime SFU & Workers.
            </p>
          </div>

          {/* Quick Join Bar */}
          <form onSubmit={handleJoinByCode} className="flex items-center space-x-2 w-full md:w-auto">
            <input
              type="text"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value)}
              placeholder="Enter meeting code..."
              className="px-4 py-2.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition w-full md:w-56"
            />
            <button
              type="submit"
              disabled={!joinCode.trim()}
              className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold text-xs rounded-xl border border-slate-700/60 transition disabled:opacity-40"
            >
              Join
            </button>
          </form>
        </div>

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Instant Meeting */}
          <div
            onClick={handleInstantMeeting}
            className="cursor-pointer group p-6 rounded-3xl bg-[#121622] border border-slate-800/80 hover:border-blue-500/50 hover:bg-slate-800/30 transition-all duration-300 shadow-xl flex items-center justify-between"
          >
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 rounded-2xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400 group-hover:scale-110 transition">
                <Video className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Start Instant Meeting</h3>
                <p className="text-xs text-slate-400 mt-0.5">Start an ad-hoc video room right away</p>
              </div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-600/30 group-hover:translate-x-1 transition">
              {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
            </div>
          </div>

          {/* Schedule Meeting */}
          <div
            onClick={() => setShowScheduleModal(true)}
            className="cursor-pointer group p-6 rounded-3xl bg-[#121622] border border-slate-800/80 hover:border-indigo-500/50 hover:bg-slate-800/30 transition-all duration-300 shadow-xl flex items-center justify-between"
          >
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 rounded-2xl bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 group-hover:scale-110 transition">
                <Calendar className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Create with Settings</h3>
                <p className="text-xs text-slate-400 mt-0.5">Set meeting title and password protection</p>
              </div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 text-slate-200 flex items-center justify-center group-hover:translate-x-1 transition">
              <Plus className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Meeting History Section */}
        <div className="space-y-4 pt-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-white">Recent Meetings</h2>
            <span className="text-xs text-slate-400">{meetings.length} total</span>
          </div>

          {loading ? (
            <div className="flex justify-center p-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : meetings.length === 0 ? (
            <div className="p-12 text-center rounded-3xl bg-[#121622] border border-slate-800/80 space-y-3">
              <Clock className="w-8 h-8 text-slate-500 mx-auto" />
              <p className="text-sm font-semibold text-slate-300">No meeting history yet</p>
              <p className="text-xs text-slate-500">Create your first meeting to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {meetings.map((m) => (
                <div
                  key={m.publicId}
                  className="p-5 rounded-2xl bg-[#121622] border border-slate-800 hover:border-slate-700 transition space-y-4 flex flex-col justify-between"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                          m.status === 'active'
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : m.status === 'scheduled'
                            ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                            : 'bg-slate-800 text-slate-400'
                        }`}
                      >
                        {m.status}
                      </span>

                      {m.hasPassword && (
                        <span className="flex items-center space-x-1 text-slate-400 text-xs" title="Password protected">
                          <Lock className="w-3.5 h-3.5" />
                        </span>
                      )}
                    </div>

                    <h3 className="font-semibold text-white text-base truncate">{m.title}</h3>
                    <p className="text-xs text-slate-500 font-mono">ID: {m.publicId}</p>
                  </div>

                  <div className="flex items-center space-x-2 pt-2 border-t border-slate-800/60">
                    <a
                      href={`#/meeting/${m.publicId}`}
                      className="flex-1 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-semibold text-center transition shadow-sm"
                    >
                      Join Meeting
                    </a>

                    <button
                      onClick={() => copyMeetingLink(m.publicId)}
                      title="Copy meeting link"
                      className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition border border-slate-700/60"
                    >
                      {copiedId === m.publicId ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#121622] border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-6">
            <h3 className="font-bold text-white text-lg">Create New Meeting</h3>

            <form onSubmit={handleCreateMeeting} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300">Meeting Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Design Review, Standup, etc."
                  required
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300 flex items-center space-x-1.5">
                  <Lock className="w-3.5 h-3.5 text-blue-400" />
                  <span>Password Protection (Optional)</span>
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Leave empty for public join"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                />
              </div>

              <div className="flex items-center space-x-3 pt-3">
                <button
                  type="button"
                  onClick={() => setShowScheduleModal(false)}
                  className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-xl shadow-md transition disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create & Join'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
